import { fromZonedTime } from "date-fns-tz";
import { bucketEventsByLocalDay } from "../../../lib/days";

// Availability-context kinds that belong in the create agenda's per-day lane.
// The candidate slots themselves are the agenda's primary rows (kind "slot"),
// so they are intentionally excluded here — this lane is the mobile echo of the
// desktop calendar's busy/mark overlay tiles, giving a phone creator the
// availability context they otherwise can't see (audit A-3).
const LANE_KINDS = new Set(["mark", "overlay-busy"]);

function pad2(n) {
  return String(n).padStart(2, "0");
}

// Local-tz midnight instant for the 1st of `date`'s month, and of the next
// month — built the SAME way lib/days anchors tz midnights (`fromZonedTime` on a
// `YYYY-MM-DDT00:00:00` wall string) so the resulting day buckets line up
// exactly with the slot day-groups, which scope by display-month.
function monthWindow(date, timeZone) {
  const y = date.getFullYear();
  const m = date.getMonth(); // 0-based
  const ny = m === 11 ? y + 1 : y;
  const nm = m === 11 ? 0 : m + 1;
  const startWall = `${y}-${pad2(m + 1)}-01T00:00:00`;
  const endWall = `${ny}-${pad2(nm + 1)}-01T00:00:00`;
  if (!timeZone) {
    return { windowStart: new Date(startWall), windowEnd: new Date(endWall) };
  }
  return {
    windowStart: fromZonedTime(startWall, timeZone),
    windowEnd: fromZonedTime(endWall, timeZone),
  };
}

function classifyKind(resource) {
  if (resource?.kind === "mark") {
    return resource.level === "PREFERRED" ? "preferred" : "feasible";
  }
  return "busy";
}

function toLaneRow(row) {
  const r = row.event.resource || {};
  const kind = classifyKind(r);
  const title =
    typeof row.event.title === "string" ? row.event.title.trim() : "";
  return {
    id: row.event.id || `${kind}:${row.dayKey}:${row.clippedStart.getTime()}`,
    kind, // "busy" | "preferred" | "feasible"
    isSelf: r.isSelf === true,
    ownerId: r.ownerId || null,
    title,
    startMs: row.clippedStart.getTime(),
    clippedStart: row.clippedStart,
    clippedEnd: row.clippedEnd,
    isContinuation: row.isContinuation,
    isAllDay: row.isAllDay,
  };
}

// Build the per-day availability lane for the create agenda. Returns a
// Map<dayKey, laneRow[]> covering ONLY the displayed month, with each day's rows
// sorted by clipped start. Multi-day spans are fanned onto every overlapped
// local day via the shared `bucketEventsByLocalDay` primitive, so the create
// agenda, the availability agenda, and the desktop month grid can never disagree
// about which day an overlay belongs to.
export function buildCreatorAgendaOverlayLane({
  events = [],
  date = new Date(),
  timeZone,
} = {}) {
  const laneEvents = events.filter((e) => {
    const r = e?.resource;
    if (!r || !LANE_KINDS.has(r.kind)) return false;
    // Drop the optimistic create placeholder spliced into the feed.
    if (r.kind === "mark" && r.markId === "__pending-new-mark__") return false;
    return true;
  });
  if (!laneEvents.length) return new Map();

  const { windowStart, windowEnd } = monthWindow(date, timeZone);
  const byDay = bucketEventsByLocalDay({
    events: laneEvents,
    timeZone,
    windowStart,
    windowEnd,
  });

  const out = new Map();
  for (const [dayKey, group] of byDay) {
    const rows = group.rows
      .map(toLaneRow)
      .sort((a, b) => a.startMs - b.startMs);
    if (rows.length) out.set(dayKey, rows);
  }
  return out;
}
